import { Box, Button, Chip, ListItem, ListItemText } from "@material-ui/core";
import ConfirmDelete from "components/ConfirmDelete";
import { ListItemAccordion } from "components/List/StyledComponents";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useState } from "react";
import Details from "../Certificates/Details";
import { FingerprintSharp, Star as StarIcon } from "@material-ui/icons";
import { EmailWithSmimeCerts, SmimeCert } from "@dashboard-v3/api";

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 15px;
`;

const ActionButtons = styled.div`
  align-self: center;
  margin-right: 20px;
`;

type Params = {
  account: EmailWithSmimeCerts;
  onDelete: () => void;
};

export default function Account({ account, onDelete }: Params) {
  const { t } = useTranslation("smime");

  return (
    <ListItem disableGutters>
      <ListItemAccordion style={{ padding: "12px 16px" }}>
        <ContentContainer>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center" mb="1rem">
                <Box fontWeight="500" fontSize="1.2rem">
                  {account.email}
                </Box>
              </Box>
            }
            secondary={<CertSelectors account={account} />}
          />
          <ActionButtons>
            <Box display="flex" gridGap="10px">
              <Button
                href={`/smime-certs/accounts/${account.email}/edit`}
                data-testid={`${account.email}__edit-item-btn`}
                variant="outlined"
                size="small"
                color="default"
                onClick={event => event.stopPropagation()}
              >
                {t("common:edit")}
              </Button>
              <ConfirmDelete
                message={t("accountDelete", {
                  name: account.email,
                })}
                onAccept={onDelete}
                label={t("common:delete")}
                variant="outlined"
                size="small"
              />
            </Box>
          </ActionButtons>
        </ContentContainer>
      </ListItemAccordion>
    </ListItem>
  );
}

function CertSelectors({ account }: { account: EmailWithSmimeCerts }) {
  const { t } = useTranslation("smime");
  const [showCertDetails, setShowCertDetails] = useState<SmimeCert | null>(
    null
  );

  const primary = account.primaryCertificate;

  const certs = (account.certificateHistory || []).sort((a, b) => {
    if (a.certificateId === primary?.certificateId) return -1;
    if (b.certificateId === primary?.certificateId) return 1;
    return b.uploadedAt - a.uploadedAt;
  });

  const withoutCerts = !primary && certs.length === 0;

  if (withoutCerts) {
    return (
      <Box display="flex" flexDirection="column" gridGap=".3rem" ml=".5rem">
        {t("noLinkedCerts")}
      </Box>
    );
  }

  return (
    <>
      <Box display="flex" gridGap=".6rem">
        {certs.map(cert => {
          const primary =
            cert.certificateId === account.primaryCertificate?.certificateId;

          return (
            <Chip
              key={cert.certificateId}
              variant="outlined"
              label={cert.friendlyName}
              icon={primary ? <StarIcon /> : <FingerprintSharp />}
              onClick={() => setShowCertDetails(cert)}
              color={primary ? "primary" : "default"}
            />
          );
        })}
      </Box>
      <Details
        cert={showCertDetails}
        open={Boolean(showCertDetails)}
        onClose={() => setShowCertDetails(null)}
      />
    </>
  );
}
